import _ from 'lodash';

import type { ChartOfAccountProps } from 'app-components/flux/chart-of-accounts/chartOfAccountsTypes';
import type { CashFlowTransaction } from 'app-components/flux/transactions/transactionsTypes';
import { getTotalNumberOfSplitsWithAllocations } from 'data/transactionList/selectors';
import { useAppSelector } from 'hooks/redux';

import { getTxnTransferHasAllocations } from '../selectors';

type Params = {
  arAccountIds: { includes: (id: string | null | undefined) => boolean };
  businessEnabled: boolean;
  coa: ChartOfAccountProps | null | undefined;
  create: boolean;
  currentTxn: CashFlowTransaction;
  disableSalesReceiptFields?: boolean;
  isAssetPayment: boolean;
  tempTxn: CashFlowTransaction;
  txn: CashFlowTransaction;
};

type HasToJS = {
  toJS: () => unknown;
};

function hasToJS(value: unknown): value is HasToJS {
  return Boolean(
    value && typeof value === 'object' && 'toJS' in value && typeof (value as { toJS?: unknown }).toJS === 'function',
  );
}

function comparableValue(value: unknown): unknown {
  return hasToJS(value) ? value.toJS() : value;
}

export function useTransactionEditGuards({
  arAccountIds,
  businessEnabled,
  coa,
  create,
  currentTxn,
  disableSalesReceiptFields,
  isAssetPayment,
  tempTxn,
  txn,
}: Params): {
  currentSplitPayments: number;
  deleteQTip: string;
  editSplitsFromExcludeBannerDisabled: boolean;
  fullDisableQBP: boolean;
  linkedExpense: boolean;
  showPaymentDialog: boolean;
  showPaymentDialogLink: boolean;
  splitDialogReadOnly: boolean;
  splitsDisabled: boolean;
} {
  const showPaymentDialogLink = useAppSelector(
    (state) => isAssetPayment && getTxnTransferHasAllocations(state, { transferId: txn?.transfer?.id }),
  );
  const currentSplitPayments = useAppSelector((state) => getTotalNumberOfSplitsWithAllocations(state, { txn }));
  const showPaymentDialog =
    businessEnabled &&
    isAssetPayment &&
    (txn.coa?.id !== currentTxn?.coa?.id ||
      !_.isEqual(comparableValue(tempTxn?.split), comparableValue(currentTxn?.split)) ||
      create);

  // `currentTxn` has stale billable links stripped only when the linked invoice is loaded and no
  // longer backs the expense. Links whose invoices are outside the current transaction window stay
  // intact so we do not unlock or unlink a potentially valid billed expense.
  const linkedExpense = Boolean(
    txn.subtype !== 'REFUND' &&
      (!!currentTxn.linkedItem?.id || currentTxn.split?.items?.some((item) => !!item.linkedItem?.id)),
  );

  const splitDialogReadOnly = Boolean(
    (businessEnabled && isAssetPayment && arAccountIds?.includes(coa?.id)) || disableSalesReceiptFields,
  );
  const fullDisableQBP = Boolean(
    disableSalesReceiptFields ||
      showPaymentDialogLink ||
      linkedExpense ||
      (txn.type === 'PAYMENT' && isAssetPayment) ||
      currentSplitPayments > 0 ||
      txn.subtype === 'SALES_RECEIPT',
  );
  const splitsDisabled = Boolean(
    (businessEnabled && isAssetPayment && arAccountIds?.includes(coa?.id)) || linkedExpense,
  );
  const editSplitsFromExcludeBannerDisabled = Boolean(
    linkedExpense || (txn.type === 'PAYMENT' && isAssetPayment) || txn.subtype === 'SALES_RECEIPT',
  );

  let deleteQTip = '';
  if (showPaymentDialogLink) {
    deleteQTip = 'Transaction can only be deleted after the payment assignment is removed';
  } else if (linkedExpense) {
    deleteQTip = 'This expense has been billed and cannot be deleted';
  } else if (currentSplitPayments > 0) {
    deleteQTip = 'This split transaction can only be deleted after the payment assignment is removed';
  }

  return {
    currentSplitPayments,
    deleteQTip,
    editSplitsFromExcludeBannerDisabled,
    fullDisableQBP,
    linkedExpense,
    showPaymentDialog,
    showPaymentDialogLink,
    splitDialogReadOnly,
    splitsDisabled,
  };
}
